/**
 * This component will be render when someone select the credit card
 * after place order same ( this) component will handle the success and failure of credit card
 * Success: load a new component which we will upload form data to Payu and show loader
 *
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer, inject } from "mobx-react";
import CreditCardNumber from "./CreditCardNumber";
import CreditCardHolderName from "./CreditCardHolderName";
import Cvv from "./Cvv";
import { Input, Toastr, Label } from "arv-reactcomponents";
import ExpMonth from "./ExpMonth";
import ExpYear from "./ExpYear";
import { PAYMENTPAGE } from "npmlinks-constants";
import Analytics from "../../../analytics";
import Event from "../../../analytics/eventFactory";
import GTM from "../../../analytics/GTM";
import PaymentGateway from "../../PaymentGateway";

@inject("CreditCardStore")
@observer
class CreditCard extends Component {
  static ShowToastr(message = PAYMENTPAGE.ERRORS.CARD_INFO_NOT_ENTERED) {
    Toastr.showToastr({
      className: "nwc-toastr-list-danger nw-toastr",
      message,
      timeout: 5000,
    });
  }
  constructor(props) {
    super(props);
    this.state = {
      isValid: false,
      expMonth: "MM",
      expYear: "YY",
      cardId: null,
      cardName: null,
      cardCvv: null,
      currentYear: new Date().getFullYear(),
      errorMessageToast: "",
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.getCardTypeInfo = this.getCardTypeInfo.bind(this);
    this.resetCardTypeInfo = this.resetCardTypeInfo.bind(this);
    this.selectedExpiryMonth = this.selectedExpiryMonth.bind(this);
    this.selectedExpiryYear = this.selectedExpiryYear.bind(this);
    this.handleIsSavedChanges = this.handleIsSavedChanges.bind(this);
  }

  componentDidMount() {
    Analytics.trackEvent({
      action: Event.action.PAYMENT_CARD_SELECTED,
      category: Event.category.CHECKOUT,
    });
  }
  get monthStart() {
    const { currentYear, expYear } = this.state;
    return currentYear === expYear ? new Date().getMonth() + 1 : 1;
  }

  /**
   * When form got valid either on form blur events, then store these information in store variable
    this.props.CreditCardStore.userEnteredCardData
   so that after validation from server binInfo and PlaceOrder we can replace Proper values
   */
  selectedExpiryMonth(selectedMonth) {
    this.setState({ expMonth: selectedMonth });
  }

  selectedExpiryYear(selectedYear) {
    this.setState({ expYear: selectedYear });
  }
  handleIsSavedChanges() {
    this.props.CreditCardStore.saveCreditCard();
  }

  handleSubmit(data) {
    let {
      isValid,
      expMonth,
      expYear,
      cardId,
      cardName,
      cardCvv,
      errorMessageToast,
    } = Object.assign({}, this.state, data);
    if (
      expYear === this.state.currentYear &&
      expMonth < new Date().getMonth() + 1
    ) {
      expMonth = "MM";
    }
    this.setState({ isValid, expMonth, expYear, cardId, cardName, cardCvv });
    if (isValid && !isNaN(expMonth) && !isNaN(expYear)) {
      GTM.pushEvent(Event.GTMEvent.CHECKOUT_PAYMENT);
      this.props.CreditCardStore.setCreditCard(
        cardId,
        cardName,
        cardCvv,
        expMonth,
        expYear,
      );
    } else {
      this.props.CreditCardStore.resetCreditCard();
      if (errorMessageToast) {
        CreditCard.ShowToastr(errorMessageToast);
      }
    }
  }

  getCardTypeInfo(binNumber) {
    return this.props.CreditCardStore.getCardTypeInfo(binNumber);
  }

  resetCardTypeInfo() {
    return this.props.CreditCardStore.resetCardTypeInfo();
  }

  render() {
    const { userEnteredCardData: { isSavedCard }, cardData = {}} = this.props.CreditCardStore;
    const { currentYear, expMonth } = this.state;
    return (
      <div className="nw-creditcard-container">
        <PaymentGateway.component
          success={this.props.CreditCardStore.successWithUserEnteredCardData}
          componentName="CreditCard"
        />
        <form name="cardform">
          <CreditCardNumber.component
            handleSubmit={cardId => this.handleSubmit(cardId)}
            getCardTypeInfo={binNumber => this.getCardTypeInfo(binNumber)}
            resetCardTypeInfo={this.resetCardTypeInfo}
            cardData={cardData}
          />
          <CreditCardHolderName.component
            handleSubmit={cardName => this.handleSubmit(cardName)}
          />
          <Cvv.component
            passedId="1"
            handleSubmit={cardCvv => this.handleSubmit(cardCvv)}
            validCvvLength={this.props.CreditCardStore.binInfo.cvvLength}
          />
          <div className="nw-creditcard-expiry">
            <Label
              htmlFor="nw-creditcard-expiry-label"
              className="nw-creditcard-expirylabel"
            >
              {PAYMENTPAGE.EXP_DATE_LABEL}
            </Label>
            <ExpMonth.component
              handleSubmit={month => this.handleSubmit(month)}
              monthStart={this.monthStart}
              selected={expMonth}
            />
            <ExpYear.component
              handleSubmit={year => this.handleSubmit(year)}
              currentYear={currentYear}
            />
          </div>
          <div className="nw-creditcard-saveoption">
            <Input
              id="nw-creditcard-saveoption"
              className="nwc-inp-checkbox-primary nwc-inp-xs nw-creditcard-savecheckbox"
              type="checkbox"
              name="isSavedCard"
              checked={isSavedCard}
              onChange={this.handleIsSavedChanges}
            />
            <Label htmlFor="nw-creditcard-saveoption">
              {PAYMENTPAGE.SAVEFORLATER_TITLE}
            </Label>
          </div>
        </form>
      </div>
    );
  }
}

CreditCard.wrappedComponent.propTypes = {
  CreditCardStore: PropTypes.shape({
    successWithUserEnteredCardData: PropTypes.shape({}).isRequired,
    userEnteredCardData: PropTypes.shape({}).isRequired,
    success: PropTypes.shape({}).isRequired,
    binInfo: PropTypes.shape({}).isRequired,
    saveCreditCard: PropTypes.func.isRequired,
    setCreditCard: PropTypes.func.isRequired,
    resetCreditCard: PropTypes.func.isRequired,
    getCardTypeInfo: PropTypes.func.isRequired,
    resetCardTypeInfo: PropTypes.func.isRequired,
    attachParamsForCard: PropTypes.func.isRequired,
    cardData: PropTypes.shape({}).isRequired,
  }).isRequired,
};

export { default as CreditCardHolderName } from "./CreditCardHolderName";
export { default as CreditCardNumber } from "./CreditCardNumber";
export { default as Cvv } from "./Cvv";
export { default as ExpMonth } from "./ExpMonth";
export { default as ExpYear } from "./ExpYear";

const getFinalComponent = component =>
  inject("CreditCardStore")(observer(component));

const ProjectComponent = {
  getFinalComponent,
  wrapper: CreditCard.wrappedComponent,
  component: CreditCard,
};

export default ProjectComponent;



// WEBPACK FOOTER //
// ./src/component/PaymentWallet/CreditCard/index.js